import styled from 'styled-components';
import {getColorFromTheme} from '@ui/ui-utils';
import {ControlButton, Panel} from '../my-objective/my-goals-v2.styles';

export const AlignmentContent = styled.section`
  --alignment-indent: clamp(24px, 3vw, 36px);
  --alignment-line: #a9abf4;
`;

export const AlignmentOverviewPanel = styled(Panel)`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  align-items: stretch;
  margin-bottom: 24px;
  overflow: hidden;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

export const AlignmentSummary = styled.div`
  min-width: 0;
  padding: 20px;

  h2 {
    margin: 0 0 14px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/20px Graphik, sans-serif;
  }

  @media (max-width: 680px) {
    border-top: 1px solid ${getColorFromTheme('borderLight')};
  }
`;

export const AlignmentBar = styled.div`
  display: flex;
  height: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: ${getColorFromTheme('borderLight')};
`;

export const AlignmentBarSegment = styled.span<{
  $tone: 'aligned' | 'unaligned';
  $width: number;
}>`
  width: ${({$width}) => $width}%;
  min-width: ${({$width}) => ($width > 0 ? '3px' : '0')};
  background: ${({$tone, theme}) =>
    $tone === 'aligned' ? theme.colors.purple300 : theme.colors.borderDark};
`;

export const AlignmentLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span {
    display: inline-flex;
    min-height: 28px;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    color: ${getColorFromTheme('textBody')};
    font: 500 12px/18px Graphik, sans-serif;
  }

  span[data-tone='aligned'] {
    border-color: ${getColorFromTheme('purple300')};
    color: ${getColorFromTheme('purple300')};
  }

  strong {
    display: grid;
    min-width: 18px;
    height: 18px;
    place-items: center;
    border-radius: 999px;
    background: ${getColorFromTheme('backgroundDark')};
    color: inherit;
    font-size: 10px;
    font-weight: 600;
  }
`;

export const AlignmentTreeSection = styled.section`
  margin-top: 10px;
`;

export const AlignmentExpandButton = styled(ControlButton)`
  &:disabled {
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const AlignmentTreeList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const AlignmentTreeItem = styled.li<{
  $nested?: boolean;
  $lastSibling?: boolean;
}>`
  position: relative;

  ${({$nested, $lastSibling}) =>
    $nested && !$lastSibling
      ? `
    &::after {
      position: absolute;
      z-index: 0;
      top: -8px;
      bottom: -8px;
      left: calc(var(--alignment-indent) * -1);
      border-left: 1px solid var(--alignment-line);
      content: '';
    }
  `
      : ''}
`;

export const AlignmentTreeChildren = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 0 9px;
  padding: 0 0 0 var(--alignment-indent);
  list-style: none;
`;

export const AlignmentTreeRow = styled.div<{
  $nested?: boolean;
  $lastSibling?: boolean;
  $hasExpandedChildren?: boolean;
}>`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;

  ${({$nested, $lastSibling}) =>
    $nested
      ? `
    &::after {
      position: absolute;
      z-index: 0;
      top: ${$lastSibling ? '-8px' : '50%'};
      left: calc(var(--alignment-indent) * -1);
      width: calc(var(--alignment-indent) + 9px);
      height: ${$lastSibling ? 'calc(50% + 8px)' : '0'};
      border-bottom: ${$lastSibling ? '1px' : '0'} solid var(--alignment-line);
      border-left: ${$lastSibling ? '1px' : '0'} solid var(--alignment-line);
      border-top: ${$lastSibling ? '0' : '1px'} solid var(--alignment-line);
      border-bottom-left-radius: ${$lastSibling ? '8px' : '0'};
      pointer-events: none;
      content: '';
    }
  `
      : ''}

  ${({$hasExpandedChildren}) =>
    $hasExpandedChildren
      ? `
    &::before {
      position: absolute;
      z-index: 0;
      top: 50%;
      bottom: -16px;
      left: 9px;
      border-left: 1px solid var(--alignment-line);
      pointer-events: none;
      content: '';
    }
  `
      : ''}
`;

export const AlignmentBranchButton = styled.button<{$expanded: boolean}>`
  position: relative;
  z-index: 1;
  display: grid;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: ${getColorFromTheme('purple300')};
  color: #ffffff;

  svg {
    width: 12px;
    height: 12px;
    transform: rotate(${({$expanded}) => ($expanded ? '0deg' : '-90deg')});
    transition: transform 160ms ease;
  }

  &:hover,
  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }
`;

export const AlignmentLeafMarker = styled.span`
  position: relative;
  z-index: 1;
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  margin: 0 3px;
  border: 4px solid ${getColorFromTheme('purple200')};
  border-radius: 50%;
  background: ${getColorFromTheme('purple300')};
`;

export const AlignmentCardSlot = styled.div<{$dimmed: boolean}>`
  min-width: 0;
  flex: 1;
  opacity: ${({$dimmed}) => ($dimmed ? 0.78 : 1)};
  transition: opacity 180ms ease;

  &:focus-within,
  &:hover {
    opacity: 1;
  }
`;

export const UnalignedHeader = styled.div`
  margin: 20px 0 8px;
  padding-top: 12px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};

  h2 {
    margin: 0;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/22px Graphik, sans-serif;
  }

  p {
    margin: 1px 0 0;
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/20px Graphik, sans-serif;
  }
`;

export const AlignmentEmpty = styled.div`
  display: grid;
  min-height: 220px;
  place-items: center;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  text-align: center;

  h2 {
    margin: 0 0 4px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 16px/24px Graphik, sans-serif;
  }

  p {
    margin: 0;
    font: 400 14px/22px Graphik, sans-serif;
  }
`;
